import apiClient from '../utils/apiClient';

export interface Job {
  id: string;
  title: string;
  description: string;
  requirements?: string;
  department?: string;
  location?: string;
  jobType?: string;
  level?: string;
  status?: string;
  postedDate?: string;
  closingDate?: string;
  createdAt?: string;
  updatedAt?: string;
  displayDescription?: string; // AI-generated short description for job cards
}

export interface JobDetail extends Job {
  hasApplied: boolean;
  applicationCount: number;
}

export interface JobFilters {
  search?: string;
  department?: string;
  location?: string;
  jobType?: string;
  page?: number;
  limit?: number;
}

export interface JobListResponse {
  jobs: Job[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class JobService {
  async getJobs(filters?: JobFilters): Promise<JobListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.department) params.append('department', filters.department);
    if (filters?.location) params.append('location', filters.location);
    if (filters?.jobType) params.append('job_type', filters.jobType);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/jobs?${params.toString()}`);
    return response.data;
  }

  async getJobById(jobId: string): Promise<JobDetail> {
    const response = await apiClient.get(`/jobs/${jobId}`);
    return response.data.data || response.data;
  }

  async applyForJob(jobId: string): Promise<any> {
    const response = await apiClient.post(`/jobs/${jobId}/apply`, {});
    return response.data;
  }

  async createInterviewSession(jobId: string): Promise<any> {
    const response = await apiClient.post(`/interviews/`, {
      jobId: jobId
    });
    return response.data;
  }

  async updateApplicationStatus(jobId: string, status: 'interview_pending' | 'interviewed' | 'reviewed' | 'accepted' | 'rejected'): Promise<any> {
    const response = await apiClient.patch(`/jobs/${jobId}/application/status`, { status });
    return response.data;
  }

  async getApplicationStatus(jobId: string): Promise<{
    hasApplied: boolean;
    applicationStatus?: string;
    appliedDate?: string;
    interviewUrl?: string;
    interviewStatus?: 'in_progress' | 'completed' | 'not_started';
  }> {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/application-status`);
      return response.data.data || response.data;
    } catch (error) {
      // If endpoint doesn't exist or user hasn't applied, return default
      return { hasApplied: false };
    }
  }

  // Mock data for development/demo
  getMockJobs(): Job[] {
    return [
      {
        id: '1',
        title: 'Senior Full Stack Developer',
        description: 'We are looking for an experienced Full Stack Developer to join our engineering team.',
        department: 'Engineering',
        location: 'Remote',
        jobType: 'Full-time',
        level: 'Senior',
        postedDate: new Date().toISOString(),
      },
      {
        id: '2',
        title: 'AI/ML Engineer',
        description: 'Join our AI team to build cutting-edge machine learning solutions.',
        department: 'AI Research',
        location: 'Singapore',
        jobType: 'Full-time',
        level: 'Mid-Senior',
        postedDate: new Date().toISOString(),
      },
      {
        id: '3',
        title: 'Product Designer',
        description: 'Create beautiful and intuitive user experiences for our products.',
        department: 'Design',
        location: 'Hybrid',
        jobType: 'Full-time',
        level: 'Mid',
        postedDate: new Date().toISOString(),
      },
      {
        id: '4',
        title: 'DevOps Engineer',
        description: 'Help us scale our infrastructure and improve our deployment processes.',
        department: 'Engineering',
        location: 'Remote',
        jobType: 'Full-time',
        level: 'Senior',
        postedDate: new Date().toISOString(),
      },
      {
        id: '5',
        title: 'Data Scientist',
        description: 'Analyze complex data sets and build predictive models.',
        department: 'Data Science',
        location: 'Singapore',
        jobType: 'Full-time',
        level: 'Mid-Senior',
        postedDate: new Date().toISOString(),
      },
      {
        id: '6',
        title: 'Frontend Developer',
        description: 'Build responsive and performant web applications using React and TypeScript.',
        department: 'Engineering',
        location: 'Remote',
        jobType: 'Contract',
        level: 'Mid',
        postedDate: new Date().toISOString(),
      },
    ];
  }
}

export default new JobService();